import { useAppStore } from '@globals/stores/app';
import { useUserStore } from '@globals/stores/user';
import { HelloResponse } from '@globals/types/Hello';
import { invokeWrapeeMethod, WrapeeMethod } from '@globals/utilities/invokeWrapeeMethod';
import serverFetch from './ServerFetch';

/**
 * Pobiera początkowe dane z serwera przy użyciu wywołania `hello.ofer`.
 */
async function getRsvp() {
	const { pathname, search, hostname } = document.location;
	const query = new URLSearchParams(search);

	// Przed pobraniem danych z hello.ofer, musimy wywołać `init-chat.ofer,
	// jeśli chcemy, by użytkownik został wlogowany. Struktura takiego adresu
	// wyglada następująco: /moje-zapytania/rfp/:rfpId/:chatId/czat?obj=xxxxxx...
	if (query.has('obj') && pathname.includes('moje-zapytania') && pathname.includes('rfp')) {
		const pathSegments = pathname.split('/');
		await fetch(`/rfp-json/init-chat.ofer?rfpId=${pathSegments[3]}&cid=${pathSegments[4]}&${query.toString()}`);
	}

	// Utwórz placeholder odpowiedzi serwera.
	let rsvp: HelloResponse = {
		baseUrl: hostname,
		flags: [],
		error: 'NOT_AUTH',
		status: 'ERROR',
		sessionId: '',
	};

	let url = `/profile/hello.ofer?${query.toString()}`;

	try {
		// Apka iOS z jakiegoś _mistycznego_ powodu próbuje pobrać zcacheowaną wersję jeszcze sprzed zalogowania.
		// Dodajemy do querystringa aktualny czas, żeby zachowywała się należycie.
		const deviceInfoResponse = await invokeWrapeeMethod(WrapeeMethod.getMobileMetrics);
		if (deviceInfoResponse.deviceType === 'Apple' && url === '/profile/hello.ofer?') {
			url = '/profile/hello.ofer?ts=' + Number(new Date());
		}
	} catch (err) {}

	rsvp = await serverFetch(url);

	// Nie rozumiem tego. Z jakiegoś powodu wywołujemy hello.ofer jeszcze raz,
	// gdy aplikacja jest aplikacją kupującego i odpowiedź z serwera jest 'NOT_AUTH'.
	const isBuyerApp = window.localStorage.getItem('isBuyerApp') === 'true';
	if ((pathname.includes('moje-zapytania') || isBuyerApp) && rsvp.error === 'NOT_AUTH') {
		rsvp = await serverFetch(`/profile/hello.ofer?${query}`, {}, 'POST');
	}

	return rsvp;
}

/**
 * Czy użytkownik powinien zostać przekierowany na stronę logowania,
 * jeżeli nie ma wystarczających uprawnień?
 */
function needsCustomerLogin(role = '', requireCustomerLogin = false) {
	return (!role || role === 'BUYER') && requireCustomerLogin;
}

// eslint-disable-next-line no-unused-vars
export type HelloCallback = (rsvp: HelloResponse) => Promise<undefined>;

/**
 * Inicjuje odpowiednie Store'y na podstawie odpowiedzi serwera.
 */
async function initStores(rsvp: HelloResponse, callback?: HelloCallback) {
	const appStore = useAppStore();
	appStore.sessionId = rsvp.sessionId;

	const userStore = useUserStore();
	userStore.update(rsvp);

	if (callback) {
		await callback(rsvp);
	}

	appStore.update(rsvp);
}

/**
 * Pobiera informacje o użytkownika i inicjuje Store'y wymagane do działania aplikacji.
 * @param requireCustomerLogin Czy użytkownik musi być zalogowanym użytkownikiem z rolą `CUSTOMER` (lub wyżej), aby przejść dalej?
 * @param callback Czy wykonać jakieś dodatkowe czynności
 * @returns Czy potrzebne jest przekierowanie na stronę logowania lub inny adres URL?
 */
export default async function hello(requireCustomerLogin = false, callback?: HelloCallback) {
	const rsvp = await getRsvp();
	const needsLogin = needsCustomerLogin(rsvp.role, requireCustomerLogin);
	initStores(rsvp, callback);
	return needsLogin;
}
